import { useTranslation } from 'react-i18next';
import { APP_DEFAULT_TZ, formatLongDate } from '@utils/dateUtils';
import type { IInvoiceRow } from '@interfaces/IFinance';
import { fmtUsd } from '../format';
import { StatusPill } from './StatusPill';
import styles from './InvoicesTab.module.css';

interface IInvoicesTabProps {
  invoices: IInvoiceRow[];
}

export function InvoicesTab({ invoices }: IInvoicesTabProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';

  return (
    <section className={styles.panel}>
      <header className={styles.panelHead}>
        <div>
          <h2 className={styles.panelTitle}>{t('finance.invoices.management')}</h2>
          <p className={styles.panelSubtitle}>{t('finance.invoices.managementSubtitle')}</p>
        </div>
        <button type="button" className={styles.newBtn}>
          <span aria-hidden="true">+</span> {t('finance.invoices.newInvoice')}
        </button>
      </header>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t('finance.invoices.colInvoiceId')}</th>
            <th>{t('finance.invoices.colContractor')}</th>
            <th className={styles.alignRight}>{t('finance.invoices.colAmount')}</th>
            <th>{t('finance.invoices.colActivity')}</th>
            <th>{t('finance.invoices.colFloor')}</th>
            <th>{t('finance.invoices.colDate')}</th>
            <th className={styles.alignCenter}>{t('finance.invoices.colStatus')}</th>
            <th className={styles.alignCenter}>{t('finance.invoices.colActions')}</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((row) => (
            <tr key={row.id}>
              <td className={styles.mono}>{row.id}</td>
              <td>{row.contractor}</td>
              <td className={`${styles.alignRight} ${styles.bold}`}>{fmtUsd(row.amountUsd)}</td>
              <td className={styles.muted}>{row.activity}</td>
              <td>{row.floor}</td>
              <td className={styles.muted}>{formatLongDate(row.date, lang, APP_DEFAULT_TZ)}</td>
              <td className={styles.alignCenter}>
                <StatusPill status={row.status} />
              </td>
              <td className={styles.alignCenter}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label={t('finance.invoices.viewAria', { id: row.id })}
                >
                  📄
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
